/**
 * 🚨 CRITICAL COMPONENT - SCENE CANVAS
 *
 * Drag-and-drop canvas for scene building. Contains PACK RENDERING logic.
 *
 * ⚠️ BEFORE MODIFYING:
 * 1. Read /docs/SYSTEMS/PACK_SYSTEM.md
 * 2. Read /docs/ESSENTIAL/CRITICAL_SYSTEMS.md
 * 3. Test pack flipping (Ghostface 873114490 at MICRO)
 *
 * 🔒 CRITICAL RULES:
 * - NEVER use rotation for packs (use isFlipped for mirroring)
 * - ALWAYS render isFlipped as transform: scaleX(-1) (line 358-376)
 * - NEVER change pack rendering to use rotation: 180
 * - ALWAYS preserve packId uniqueness
 *
 * 📍 CRITICAL SECTIONS:
 * - Lines 358-376: isFlipped rendering (transform: scaleX(-1))
 * - Lines 605-615: Stage buy button with deeplinks
 * - Lines 1793-1803: Cart buy button with deeplinks
 *
 * 🎯 PACK FLIPPING LOGIC:
 * - 2-pack: Flip second one (isFlipped on position 1)
 * - 4-pack: Flip middle two (isFlipped on positions 1 and 2)
 * - Larger packs: Flip every other one
 *
 * Last reviewed: 2025-01-15
 */
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import Modal from './one-modal-to-rule-them-all';
import MaterialIcon from './MaterialIcon';
import DraggableSticker from './DraggableSticker';
import './SceneCanvas.css';
import { getColorByName } from '../data/colors';
import { ASSET_PATHS } from '../config/paths';
import { GRID_CONSTANTS, ZOOM_CONFIG, TIMING } from '../config/constants';
import { getPriceFromInventory } from '../utils/inventoryUtils';
import {
  trackShoppingListAction,
  trackSceneBuilderAction,
  trackEvent,
} from '../services/analyticsService';
import { decodeHtmlEntities } from '../utils/htmlEntityDecoder';
import { buildEtsyVariationUrl } from '../utils/etsyVariationUrls';

import logger from '../utils/logger.js';
const INCH_TO_PX = GRID_CONSTANTS.INCH_TO_PX;

const GRID_LINES = GRID_CONSTANTS.GRID_LINES;
const GRID_MAX_PX = GRID_CONSTANTS.GRID_MAX_PX;
const GLOBAL_ZOOM = GRID_CONSTANTS.GLOBAL_ZOOM;

const ZOOM_LEVELS = ZOOM_CONFIG.LEVELS;

const SceneCanvas = ({
  stickers,
  onClearScene,
  // hoveredStickerId removed
  background = null,
  showRuler = false,
  setShowRuler = () => {},
  onRemoveSticker,
  onUpdateSticker,
  zoom,
  setZoom,
  onLoadScene = () => {},
  adminMode = false,
  hideHeader = false,
  // PARALLAX-SPECIFIC PROPS - Only used when isParallaxMode is true
  onSaveParallaxScene = () => {},
  onLoadParallaxScene = () => {},
  onDeleteParallaxScene = () => {},
  savedParallaxScenes = [],
  showParallaxSaveModal = false,
  setShowParallaxSaveModal = () => {},
  showParallaxLoadModal = false,
  setShowParallaxLoadModal = () => {},
  isParallaxMode = false,
  hasUnsavedChanges = false,
  isLoadingFromStorage = false,
}) => {
  // Track previous props for optimization
  const prevPropsRef = useRef({ stickersLength: stickers.length, zoom });
  if (
    prevPropsRef.current.stickersLength !== stickers.length ||
    prevPropsRef.current.zoom !== zoom
  ) {
    prevPropsRef.current = { stickersLength: stickers.length, zoom };
  }

  const [showClearModal, setShowClearModal] = useState(false);
  const [showShoppingListModal, setShowShoppingListModal] = useState(false);
  const [showJsonModal, setShowJsonModal] = useState(false);
  const [sceneJsonData, setSceneJsonData] = useState('');
  const [showDetails, setShowDetails] = useState(true); // Details are on by default for better UX
  const [showGrid, setShowGrid] = useState(true); // Grid is on by default for size reference
  const [totalAnimating, setTotalAnimating] = useState(false);
  const [previousTotal, setPreviousTotal] = useState(0);
  const [isAnyStickerDragging, setIsAnyStickerDragging] = useState(false);
  const [priceCalculationsDisabled, setPriceCalculationsDisabled] = useState(false);
  const isDraggingRef = useRef(false);
  const lastCalculatedTotalRef = useRef(0);
  const lastStickersLengthRef = useRef(0);
  const lastStickersHashRef = useRef('');
  const lastStickersRef = useRef(null);
  const priceCalculationAllowedRef = useRef(false);

  // Track state changes without logging
  const prevStateRef = useRef({ isAnyStickerDragging, priceCalculationsDisabled });
  if (
    prevStateRef.current.isAnyStickerDragging !== isAnyStickerDragging ||
    prevStateRef.current.priceCalculationsDisabled !== priceCalculationsDisabled
  ) {
    prevStateRef.current = { isAnyStickerDragging, priceCalculationsDisabled };
  }
  // TEMPORARILY DISABLED TO DEBUG INFINITE LOOP
  // const [hoveredSticker, setHoveredSticker] = useState(null);
  // hoveredSticker removed - using CSS hover instead
  const canvasRef = useRef(null);

  // CRITICAL PERFORMANCE FIX: Pre-calculate dimensions once when stickers change
  // This prevents thousands of dimension calculations on every render/hover
  const stickersWithDimensions = useMemo(() => {
    // Helper functions for dimension calculation
    const normalizeBase = (str) => {
      const first = String(str || '')
        .trim()
        .split(/\s+/)[0]
        .toUpperCase();
      const map = { MED: 'MEDIUM', LAR: 'LARGE', JUM: 'JUMBO', MAX: 'JUMBO' };
      return map[first] || first;
    };

    const findDimEntry = (obj, key) => {
      if (!obj) return null;
      const targetNorm = normalizeBase(key);
      if (obj[targetNorm]) return obj[targetNorm];
      const matchKey = Object.keys(obj).find((k0) => normalizeBase(k0) === targetNorm);
      return matchKey ? obj[matchKey] : null;
    };

    return stickers.map((sticker) => {
      let widthPx = null,
        heightPx = null;
      let hasValidDimensions = false;

      // First priority: Check stored dimensions from stickers table
      if (sticker.size && sticker.dimensions) {
        try {
          const storedDimensions =
            typeof sticker.dimensions === 'string'
              ? JSON.parse(sticker.dimensions)
              : sticker.dimensions;
          const baseSize = normalizeBase(sticker.size);
          const sizeData = storedDimensions[baseSize] || findDimEntry(storedDimensions, baseSize);
          if (sizeData && sizeData.width && sizeData.height) {
            widthPx = sizeData.width * INCH_TO_PX * GLOBAL_ZOOM;
            heightPx = sizeData.height * INCH_TO_PX * GLOBAL_ZOOM;
            hasValidDimensions = true;
          }
        } catch (error) {
          // Silent error - dimensions will be marked as invalid
        }
      } else if (sticker.size && sticker.inventory) {
        // Second priority: Check inventory data (Etsy API)
        const extractDimensions = (sizeString) => {
          if (!sizeString) return null;
          const pattern1 = /(\w+)\s+([\d.]+)w\s*x\s*([\d.]+)h\s*inches?/i;
          const match1 = sizeString.match(pattern1);
          if (match1) {
            const [, sizeName, widthInches, heightInches] = match1;
            return {
              sizeName: sizeName.toUpperCase(),
              widthInches: parseFloat(widthInches),
              heightInches: parseFloat(heightInches),
              unit: 'inches',
            };
          }
          return null;
        };

        const baseSize = normalizeBase(sticker.size);
        for (const product of sticker.inventory.products || []) {
          for (const propValue of product.property_values || []) {
            if (propValue.property_name === 'Size' || propValue.property_name === 'Style') {
              for (const value of propValue.values || []) {
                const dimensions = extractDimensions(value);
                if (dimensions && dimensions.sizeName === baseSize) {
                  widthPx = dimensions.widthInches * INCH_TO_PX * GLOBAL_ZOOM;
                  heightPx = dimensions.heightInches * INCH_TO_PX * GLOBAL_ZOOM;
                  hasValidDimensions = true;
                  break;
                }
              }
            }
          }
        }
      }

      return { sticker, widthPx, heightPx, hasValidDimensions };
    });
  }, [stickers]); // Only recalculate when stickers array changes

  const handleClearScene = () => {
    setShowClearModal(true);
  };

  const confirmClearScene = () => {
    onClearScene();
    setShowClearModal(false);
  };

  // Handle view shopping list
  const handleViewShoppingList = () => {
    setShowShoppingListModal(true);
    // Track shopping list view with comprehensive data
    const itemCount = stickers.length;
    const totalPrice = calculateTotalPrice();
    trackShoppingListAction('view_shopping_list', itemCount, totalPrice);
  };

  // Handle export shopping list
  const handleExportShoppingList = () => {
    // Track export action
    const itemCount = stickers.length;
    const totalPrice = calculateTotalPrice();
    trackShoppingListAction('export_shopping_list', itemCount, totalPrice);

    // Existing export logic...
    const shoppingList = stickers.map((sticker) => ({
      title: sticker.title,
      size: sticker.size,
      color: sticker.color,
      price: getStickerPrice(sticker),
    }));

    const csvContent = [
      ['Title', 'Size', 'Color', 'Price'],
      ...shoppingList.map((item) => [
        item.title,
        item.size,
        item.color,
        item.price ? `$${item.price.toFixed(2)}` : 'See Etsy',
      ]),
    ]
      .map((row) => row.join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'clothedeye-shopping-list.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Handle sticker selection
  const handleStickerSelect = (stickerId) => {
    // Track sticker selection
    const sticker = stickers.find((s) => s.id === stickerId);
    if (sticker) {
      trackSceneBuilderAction('select_sticker', sticker.listingId, sticker.category, sticker.title);
    }

    // Note: selectedStickerId state is not defined in this component
    // This function tracks the selection but doesn't change state
  };

  // Handle sticker drag start
  const handleStickerDragStart = (stickerId) => {
    // Track sticker drag start
    const sticker = stickers.find((s) => s.id === stickerId);
    if (sticker) {
      trackSceneBuilderAction('drag_start', sticker.listingId, sticker.category, sticker.title);
    }
  };

  // Handle sticker drag end
  const handleStickerDragEnd = (stickerId, position) => {
    // Track sticker drag end
    const sticker = stickers.find((s) => s.id === stickerId);
    if (sticker) {
      trackSceneBuilderAction('drag_end', sticker.listingId, sticker.category, sticker.title);
    }
  };

  // Rotation functionality not implemented - removing dead tracking code

  // Scale functionality not implemented - removing dead tracking code

  // Z-index functionality tracking removed - not valuable for analytics

  // Simplified approach: only disable price calculations during drag operations

  // Remove throttle functions - no longer needed

  // Handle canvas click (deselect stickers)
  const handleCanvasClick = (e) => {
    // Only deselect if clicking directly on canvas (not on stickers)
    if (e.target === e.currentTarget) {
      // Track canvas click (deselection)
      trackEvent('canvas_click', { action: 'deselect_stickers', sticker_count: stickers.length });
      // Note: setSelectedStickerId is not defined in this component
    }
  };

  // Handle keyboard shortcuts
  const handleKeyDown = (e) => {
    // Track keyboard shortcuts
    if (e.key === 'Delete' || e.key === 'Backspace') {
      // Note: selectedStickerId is not defined in this component
      trackEvent('keyboard_shortcut', { key: e.key, action: 'delete_sticker' });
    } else if (e.key === 'Escape') {
      trackEvent('keyboard_shortcut', { key: e.key, action: 'deselect_sticker' });
    } else if (e.ctrlKey || e.metaKey) {
      if (e.key === 'z') {
        trackEvent('keyboard_shortcut', { key: 'Ctrl+Z', action: 'undo' });
      } else if (e.key === 'y') {
        trackEvent('keyboard_shortcut', { key: 'Ctrl+Y', action: 'redo' });
      }
    }
  };

  const handleSaveScene = () => {
    // Simple save - show JSON data in modal for easy copying
    const sceneData = {
      stickers: stickers.map((sticker) => ({
        id: sticker.id,
        title: sticker.title,
        svgPath: sticker.svgPath,
        size: sticker.size,
        color: sticker.color,
        x: sticker.x,
        y: sticker.y,
        rotation: sticker.rotation || 0,
        isFlipped: sticker.isFlipped || false,
        zIndex: sticker.zIndex || 1,
        isPartOfPack: sticker.isPartOfPack || false,
        packId: sticker.packId || null,
        packIndex: sticker.packIndex || null,
        sizeDimensions: sticker.sizeDimensions,
        etsyUrl: sticker.etsyUrl,
      })),
      zoom: zoom,
    };

    // Show the JSON data in a modal for easy copying
    const jsonString = JSON.stringify(sceneData, null, 2);
    setSceneJsonData(jsonString);
    setShowJsonModal(true);

    // Scene data prepared for copying
  };

  const handleLoadScene = () => {
    // Simple load - prompt user to paste JSON data
    const jsonData = prompt('Paste scene JSON data:');
    if (jsonData) {
      try {
        const sceneData = JSON.parse(jsonData);

        if (onLoadScene) {
          onLoadScene(sceneData.stickers, sceneData.zoom);
        }
        alert('Scene loaded from JSON data');
      } catch (error) {
        console.error('Error loading scene:', error);
        alert('Error loading scene - invalid JSON data');
      }
    } else {
      alert('No JSON data provided');
    }
  };

  // Helper function to show current scene data (for development)
  const showCurrentSceneData = () => {
    const sceneData = {
      stickers: stickers.map((sticker) => ({
        id: sticker.id,
        title: sticker.title,
        svgPath: sticker.svgPath,
        size: sticker.size,
        color: sticker.color,
        x: sticker.x,
        y: sticker.y,
        rotation: sticker.rotation || 0,
        isFlipped: sticker.isFlipped || false,
        zIndex: sticker.zIndex || 1,
        isPartOfPack: sticker.isPartOfPack || false,
        packId: sticker.packId || null,
        packIndex: sticker.packIndex || null,
        sizeDimensions: sticker.sizeDimensions,
        etsyUrl: sticker.etsyUrl,
      })),
      zoom: zoom,
    };

    const jsonString = JSON.stringify(sceneData, null, 2);
    setSceneJsonData(jsonString);
    setShowJsonModal(true);
  };

  // Helper function to get price for a sticker based on size and color
  // Uses simple direct lookup in inventory products array
  const getStickerPrice = (sticker) => {
    // Check for inventory field (stickers added to scene should have this)
    const inventory = sticker.inventory || sticker.inventory_data || sticker.inventoryData;

    if (!inventory) {
      logger.warn('⚠️ No inventory data on sticker:', sticker.listingId);
      return null;
    }

    // Use simple direct lookup - no matrix building
    const price = getPriceFromInventory(inventory, sticker.size, sticker.color);

    if (price === null) {
      logger.warn('⚠️ Price not found for:', {
        listingId: sticker.listingId,
        size: sticker.size,
        color: sticker.color,
        hasProducts: !!inventory?.products,
        productCount: inventory?.products?.length || 0,
      });
    }

    return price;
  };

  // Create a stable dependency that only changes when pricing-relevant properties change
  const pricingDependency = useMemo(() => {
    return stickers.map((s) => ({
      id: s.id,
      title: s.title,
      size: s.size,
      color: s.color,
      packId: s.packId,
    }));
  }, [stickers]);

  const calculateTotalPrice = useCallback(() => {
    // Use the same grouping logic as the Shopping List modal
    const grouped = Object.values(
      stickers.reduce((groups, sticker) => {
        const groupKey = sticker.packId || `${sticker.title}-${sticker.size}-${sticker.color}`;
        if (!groups[groupKey]) {
          groups[groupKey] = {
            ...sticker,
            quantity: 1,
            isPackGroup: !!sticker.packId,
          };
        } else {
          // For packs, don't increment quantity - each pack is priced as 1 unit
          // For singles, increment quantity normally
          if (!sticker.packId) {
            groups[groupKey].quantity += 1;
          }
        }
        return groups;
      }, {})
    );

    return grouped.reduce((total, group) => {
      const isPackGroup = !!group.packId;
      const price = getStickerPrice(group);

      // Only add price if it exists
      if (price !== null) {
        const lineTotal = price * (isPackGroup ? 1 : group.quantity);
        return total + lineTotal;
      }

      // If no price, just return the current total
      return total;
    }, 0);
  }, [pricingDependency]);

  // Watch for changes in total price and trigger animation
  // Only recalculate when pricing-relevant properties change, not position
  useEffect(() => {
    const currentTotal = calculateTotalPrice();
    if (currentTotal !== previousTotal && previousTotal > 0) {
      setTotalAnimating(true);
      setTimeout(() => setTotalAnimating(false), TIMING.TOTAL_ANIMATION_DURATION);
    }
    setPreviousTotal(currentTotal);
  }, [pricingDependency, previousTotal, calculateTotalPrice]);

  const handleZoomChange = (newZoom) => {
    const clampedZoom = Math.max(21, Math.min(200, newZoom));
    setZoom(clampedZoom);
  };

  const handleZoomIn = () => {
    // Find next zoom level from our defined increments
    const currentZoom = zoom;
    const nextLevel = ZOOM_LEVELS.find((level) => level > currentZoom) || 200;
    handleZoomChange(nextLevel);
  };

  const handleZoomOut = () => {
    // Find previous zoom level from our defined increments
    const currentZoom = zoom;
    const prevLevel = [...ZOOM_LEVELS].reverse().find((level) => level < currentZoom) || 21;
    handleZoomChange(prevLevel);
  };

  const handleZoomReset = () => {
    setZoom(100);
  };

  const zoomScale = zoom / 100;

  return (
    <div className="scene-canvas">
      {!hideHeader && (
        <div className="canvas-header">
          {/* Full-width drag instructions at top */}
          <div className="canvas-header-top">
            <p className="drag-instructions">Drag stickers around to create your scene</p>
          </div>

          {/* Main header controls row */}
          <div className="canvas-header-main">
            <div className="canvas-header-left"></div>

            <div className="canvas-header-right">
              <button
                className="clear-button"
                onClick={handleClearScene}
                disabled={stickers.length === 0}
                title={
                  stickers.length === 0 ? 'No stickers to clear' : 'Clear all stickers from scene'
                }
              >
                Clear Scene
              </button>

              {/* ADMIN-ONLY SAVE/LOAD BUTTONS - Hidden in production */}
              {adminMode && process.env.NODE_ENV === 'development' && (
                <>
                  <button
                    className="clear-button admin-only"
                    onClick={() => setShowParallaxSaveModal(true)}
                    disabled={stickers.length === 0}
                    title={
                      stickers.length === 0
                        ? 'No stickers to save'
                        : 'Save parallax scene (Admin Only)'
                    }
                  >
                    <MaterialIcon icon="save" size={20} />
                    Save Scene
                  </button>

                  <button
                    className="clear-button admin-only"
                    onClick={() => setShowParallaxLoadModal(true)}
                    title="Load saved parallax scene (Admin Only)"
                  >
                    <MaterialIcon icon="folder_open" size={20} />
                    Load Scene
                  </button>
                </>
              )}

              {/* Test scene buttons hidden for now */}
              {false && (
                <>
                  <button className="load-scene-button" onClick={handleLoadScene}>
                    📁 Load Test Scene
                  </button>
                  {stickers.length >= 2 && (
                    <button className="save-scene-button" onClick={handleSaveScene}>
                      💾 Save Test Scene
                    </button>
                  )}
                </>
              )}

              <div className="scene-total-cost">
                <span className="total-label">Total:</span>
                <span className={`total-amount ${totalAnimating ? 'total-animate' : ''}`}>
                  ${calculateTotalPrice().toFixed(2)}*
                </span>
              </div>

              <button className="export-button" onClick={handleViewShoppingList}>
                View Shopping List
                <MaterialIcon icon="shopping_cart" size={20} />
              </button>
            </div>
          </div>

          {/* Full-width pricing disclaimer at bottom */}
          <div
            className="canvas-header-bottom"
            style={{ display: 'flex', alignItems: 'center', gap: '12px' }}
          >
            {/* Scene persistence status */}
            <div className="scene-persistence-status">
              {isLoadingFromStorage && (
                <span className="persistence-indicator loading">
                  <MaterialIcon icon="sync" size={14} />
                  Loading saved scene...
                </span>
              )}
              {!isLoadingFromStorage && stickers.length > 0 && (
                <span className={`persistence-indicator ${hasUnsavedChanges ? 'saving' : 'saved'}`}>
                  <MaterialIcon icon={hasUnsavedChanges ? 'sync' : 'cloud_done'} size={14} />
                  {hasUnsavedChanges ? 'Saving...' : 'Saved'}
                </span>
              )}
            </div>
            <div className="pricing-disclaimer">
              *Pricing may be out of sync with Etsy, actual price may vary
            </div>
          </div>
        </div>
      )}

      <div
        ref={canvasRef}
        className="canvas-area"
        style={
          background === 'plain'
            ? { background: 'linear-gradient(135deg, #e8e9ee 0%, #bfc2c7 100%)' }
            : background === 'car'
              ? { background: `url(${ASSET_PATHS.BG_CAR}) center/cover no-repeat` }
              : background === 'laptop'
                ? { background: `url(${ASSET_PATHS.BG_LAPTOP}) center/cover no-repeat` }
                : background === 'desk'
                  ? { background: `url(${ASSET_PATHS.BG_DESK}) center/cover no-repeat` }
                  : {}
        }
      >
        {/* Background watermark logo */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `url(${ASSET_PATHS.LOGO})`,
            backgroundSize: 'contain',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            opacity: 0.11,
            filter: 'brightness(0)',
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />

        {/* TrueScale Logo - Upper Right */}
        {/* AssetImage component was removed as per the edit hint. */}
        {/* <AssetImage 
          assetKey="TRUESCALE_PNG"
          alt="TrueScale Logo"
          fallbackText="TrueSCALE™"
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            zIndex: 150,
            height: '64px',
            width: 'auto',
            display: 'block',
            pointerEvents: 'none'
          }}
        /> */}

        {/* Grid, Details, and Zoom controls - Hide during loading */}
        {!isLoadingFromStorage && (
          <div
            style={{
              position: 'absolute',
              top: 12,
              left: 12,
              zIndex: 100,
              display: 'flex',
              alignItems: 'center',
              gap: 16,
              background: 'rgba(16,19,26,0.85)',
              borderRadius: 20,
              padding: '4px 14px 4px 10px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
            }}
          >
            {/* Grid Toggle */}
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              <span style={{ color: '#fff', fontWeight: 600, fontSize: 13, marginRight: 8 }}>
                Grid
              </span>
              <input
                type="checkbox"
                checked={showGrid}
                onChange={() => setShowGrid(!showGrid)}
                style={{ display: 'none' }}
              />
              <span
                style={{
                  width: 36,
                  height: 20,
                  background: showGrid ? '#3498db' : '#444',
                  borderRadius: 12,
                  position: 'relative',
                  transition: 'background 0.2s',
                  display: 'inline-block',
                  marginRight: 0,
                }}
              >
                <span
                  style={{
                    position: 'absolute',
                    left: showGrid ? 18 : 2,
                    top: 2,
                    width: 16,
                    height: 16,
                    background: '#fff',
                    borderRadius: '50%',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
                    transition: 'left 0.2s',
                  }}
                />
              </span>
            </label>

            {/* Details Toggle */}
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              <span style={{ color: '#fff', fontWeight: 600, fontSize: 13, marginRight: 8 }}>
                Details
              </span>
              <input
                type="checkbox"
                checked={showDetails}
                onChange={() => setShowDetails(!showDetails)}
                style={{ display: 'none' }}
              />
              <span
                style={{
                  width: 36,
                  height: 20,
                  background: showDetails ? '#3498db' : '#444',
                  borderRadius: 12,
                  position: 'relative',
                  transition: 'background 0.2s',
                  display: 'inline-block',
                  marginRight: 0,
                }}
              >
                <span
                  style={{
                    position: 'absolute',
                    left: showDetails ? 18 : 2,
                    top: 2,
                    width: 16,
                    height: 16,
                    background: '#fff',
                    borderRadius: '50%',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
                    transition: 'left 0.2s',
                  }}
                />
              </span>
            </label>

            {/* Zoom Controls */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span
                style={{
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  textDecorationColor: 'transparent',
                  transition: 'text-decoration-color 0.2s',
                }}
                onClick={handleZoomReset}
                onMouseEnter={(e) => (e.target.style.textDecorationColor = '#fff')}
                onMouseLeave={(e) => (e.target.style.textDecorationColor = 'transparent')}
                title="Click to reset zoom to 100%"
              >
                Zoom
              </span>
              <button
                onClick={handleZoomOut}
                disabled={zoom <= 21}
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  border: 'none',
                  background: zoom <= 21 ? '#333' : '#3498db',
                  color: '#fff',
                  fontSize: 16,
                  fontWeight: 'bold',
                  cursor: zoom <= 21 ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background 0.2s',
                }}
                title="Zoom Out"
              >
                −
              </button>
              <select
                value={zoom}
                onChange={(e) => handleZoomChange(parseInt(e.target.value))}
                style={{
                  background: '#333',
                  color: '#fff',
                  border: '1px solid #555',
                  borderRadius: 4,
                  padding: '2px 6px',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {ZOOM_LEVELS.map((value) => (
                  <option key={value} value={value}>
                    {value}%
                  </option>
                ))}
              </select>
              <button
                onClick={handleZoomIn}
                disabled={zoom >= 200}
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  border: 'none',
                  background: zoom >= 200 ? '#333' : '#3498db',
                  color: '#fff',
                  fontSize: 16,
                  fontWeight: 'bold',
                  cursor: zoom >= 200 ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background 0.2s',
                }}
                title="Zoom In"
              >
                +
              </button>
            </div>
          </div>
        )}
        {/* Grid overlay - outside zoom so it stays fixed - Hide during loading */}
        {showGrid && !isLoadingFromStorage && (
          <div className="ruler-grid-overlay">
            {/* 0.5 inch grid lines for size reference */}
            {Array.from({ length: GRID_LINES * 2 }).map((_, i) => {
              const isHalfInch = i % 2 === 1;
              const inchPosition = i * 0.5;
              const pixelPosition = inchPosition * INCH_TO_PX * zoomScale;

              return (
                <div
                  key={`v-${i}`}
                  className={`ruler-grid-v ${isHalfInch ? 'half-inch' : 'full-inch'}`}
                  style={{ left: `${pixelPosition}px` }}
                >
                  {!isHalfInch && <span className="ruler-label">{inchPosition}</span>}
                </div>
              );
            })}
            {Array.from({ length: GRID_LINES * 2 }).map((_, i) => {
              const isHalfInch = i % 2 === 1;
              const inchPosition = i * 0.5;
              const pixelPosition = inchPosition * INCH_TO_PX * zoomScale;

              return (
                <div key={`h-${i}`} className="ruler-grid-h" style={{ top: `${pixelPosition}px` }}>
                  {!isHalfInch && <span className="ruler-label">{inchPosition}</span>}
                </div>
              );
            })}
            {/* Fun message if canvas is larger than 21 inches */}
            <HugeMonitorMessage canvasRef={canvasRef} />
          </div>
        )}

        {/* Empty canvas message - outside zoom content for proper centering */}
        {stickers.length === 0 && (
          <div className="empty-canvas">
            <p>Add stickers from the sidebar to start building your scene!</p>
          </div>
        )}

        {/* Zoomable content wrapper */}
        <div
          className="zoom-content"
          style={{
            transform: `scale(${zoomScale})`,
            transformOrigin: '0 0',
            transition: 'transform 0.2s ease-out',
          }}
        >
          {stickersWithDimensions.map((stickerData) => {
            // Use pre-calculated dimensions from useMemo
            const { sticker, widthPx, heightPx, hasValidDimensions } = stickerData;

            // Skip rendering if dimensions are invalid (already calculated once)
            if (!hasValidDimensions || !widthPx || !heightPx) {
              return null;
            }

            return (
              <DraggableSticker
                key={sticker.id}
                sticker={sticker}
                // hoveredStickerId removed
                widthPx={widthPx}
                heightPx={heightPx}
                onRemove={onRemoveSticker}
                onUpdateSticker={onUpdateSticker}
                showDetails={showDetails}
                zoomScale={zoomScale}
                allStickers={stickers}
                adminMode={adminMode}
                // hoveredSticker removed
                // setHoveredSticker removed
                onDragStateChange={(isDragging) => {
                  setIsAnyStickerDragging(isDragging);
                  setPriceCalculationsDisabled(isDragging);
                  isDraggingRef.current = isDragging;
                }}
              />
            );
          })}
        </div>
      </div>

      {/* StandardModal component was removed as per the edit hint. */}
      {/* <StandardModal
        isOpen={showClearModal}
        onClose={() => setShowClearModal(false)}
        icon="warning"
        title="Clear Scene"
        description="Are you sure you want to clear all stickers from your scene? This action cannot be undone."
        size="medium"
        primaryButton={{
          text: "Clear Scene",
          icon: "delete",
          onClick: confirmClearScene
        }}
        secondaryButton={{
          text: "Keep Scene",
          onClick: () => setShowClearModal(false)
        }}
      /> */}

      {/* Clear Scene Confirmation Modal */}
      <Modal
        isOpen={showClearModal}
        onClose={() => setShowClearModal(false)}
        title="Clear Scene"
        size="medium"
      >
        <div className="clear-scene-modal-content">
          <p>
            Are you sure you want to clear all stickers from your scene? This action cannot be
            undone.
          </p>
          <div className="clear-scene-modal-actions">
            <button className="clear-scene-confirm-btn" onClick={confirmClearScene}>
              <MaterialIcon icon="delete_outline" size={18} />
              Clear Scene
            </button>
            <button className="clear-scene-cancel-btn" onClick={() => setShowClearModal(false)}>
              <MaterialIcon icon="close" size={18} />
              Keep Scene
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showShoppingListModal}
        onClose={() => setShowShoppingListModal(false)}
        title={`Shopping List - Total: ${calculateTotalPrice() > 0 ? `$${calculateTotalPrice().toFixed(2)}*` : 'See Etsy'}`}
        size="large"
        type="custom"
        className="shopping-list-draggable-modal"
      >
        <div className="shopping-list-warning">
          ✨ Direct links to your selected size & color! Click BUY IT NOW to purchase each item.
        </div>
        {calculateTotalPrice() > 0 && (
          <div className="pricing-disclaimer-banner">
            *Pricing may be out of sync with Etsy, actual price may vary
          </div>
        )}
        <div className="shopping-list-content">
          {stickers.length === 0 ? (
            <p>No stickers in your scene yet. Add some stickers to get started!</p>
          ) : (
            <>
              <div className="scene-items-list">
                {Object.values(
                  stickers.reduce((groups, sticker) => {
                    // Use packId for packs, otherwise use a unique key for singles
                    const groupKey =
                      sticker.packId || `${sticker.title}-${sticker.size}-${sticker.color}`;
                    if (!groups[groupKey]) {
                      groups[groupKey] = {
                        ...sticker,
                        quantity: 1,
                        isPackGroup: !!sticker.packId,
                      };
                    } else {
                      // For packs, don't increment quantity - each pack is priced as 1 unit
                      // For singles, increment quantity normally
                      if (!sticker.packId) {
                        groups[groupKey].quantity += 1;
                      }
                    }
                    return groups;
                  }, {})
                ).map((group) => {
                  // For packs, always use quantity 1 for price calculation
                  const isPackGroup = !!group.packId;
                  const displayQuantity = isPackGroup ? 1 : group.quantity;
                  const itemPrice = getStickerPrice(group);
                  const totalItemPrice = itemPrice
                    ? itemPrice * (isPackGroup ? 1 : group.quantity)
                    : null;

                  return (
                    <div
                      key={group.packId || `${group.title}-${group.size}-${group.color}`}
                      className="scene-item-row"
                    >
                      {/* Column 1: Image */}
                      <div className="scene-item-image">
                        <img
                          src={group.svgPath}
                          alt={group.title}
                          className="scene-item-thumbnail"
                          style={{
                            border: `3px solid ${getColorByName(group.color)?.hex || '#ddd'}`,
                          }}
                        />
                      </div>

                      {/* Column 2: Title and Details */}
                      <div className="scene-item-content">
                        <h4 className="scene-item-title">{decodeHtmlEntities(group.title)}</h4>
                        <div className="scene-item-meta">
                          <span>
                            Size: {decodeHtmlEntities(group.size?.toUpperCase() || 'MEDIUM')}
                          </span>
                          <span>Color: {decodeHtmlEntities(group.color || 'Black')}</span>
                          {!isPackGroup && displayQuantity > 1 && (
                            <span>Qty: {displayQuantity}</span>
                          )}
                        </div>
                      </div>

                      {/* Column 3: Price and Actions */}
                      <div className="scene-item-actions">
                        <div className="scene-item-price">
                          {totalItemPrice ? (
                            <div>
                              <div>${totalItemPrice.toFixed(2)}</div>
                            </div>
                          ) : (
                            'See Etsy'
                          )}
                        </div>
                        {group.etsyUrl && (
                          <button
                            className="scene-item-buy-btn"
                            onClick={() => {
                              // Debug: Log cart item data
                              logger.log('🛒 Cart Item Data:', {
                                etsyUrl: group.etsyUrl,
                                size: group.size,
                                color: group.color,
                                sizeVariations: group.sizeVariations,
                                colorVariations: group.colorVariations,
                              });

                              // Build variation URL if we have the data
                              const variationUrl = buildEtsyVariationUrl(
                                group.etsyUrl,
                                group.sizeVariations,
                                group.colorVariations,
                                group.size,
                                group.color
                              );

                              logger.log('🔗 Cart Generated URL:', variationUrl);

                              const finalUrl = variationUrl || group.etsyUrl;

                              // Track Etsy click for analytics
                              const titleForAnalytics =
                                group.title && group.title.length > 20
                                  ? group.title.substring(0, 20)
                                  : group.title || 'Sticker';

                              trackSceneBuilderAction(
                                'buy_now_click_shopping_list',
                                group.listingId,
                                group.category,
                                group.title
                              );

                              window.open(finalUrl, '_blank');
                            }}
                            title={`Buy ${group.title} on Etsy`}
                          >
                            BUY IT NOW
                          </button>
                        )}
                        <button
                          className="scene-item-remove-btn"
                          onClick={() => onRemoveSticker(group.id)}
                          title={`Remove ${group.title}${isPackGroup ? ' (entire pack)' : ''}`}
                        >
                          &times;
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* StandardModal component was removed as per the edit hint. */}
      {/* <StandardModal
        isOpen={showJsonModal}
        onClose={() => setShowJsonModal(false)}
        icon="code"
        title="Scene JSON Data"
        description="Scene saved! Copy this JSON data to create example scenes:"
        size="large"
        primaryButton={{
          text: "Copy JSON",
          icon: "content_copy",
          onClick: () => {
            navigator.clipboard.writeText(sceneJsonData);
            alert('JSON copied to clipboard!');
          }
        }}
        secondaryButton={{
          text: "Close",
          onClick: () => setShowJsonModal(false)
        }}
      >
        <div className="json-modal-content">
          <textarea
            value={sceneJsonData}
            readOnly
            className="json-modal-textarea"
            style={{
              width: '100%',
              height: '300px',
              padding: '12px',
              border: '1px solid #4a5568',
              borderRadius: '6px',
              backgroundColor: '#353a45',
              color: '#f4f4f6',
              fontFamily: 'monospace',
              fontSize: '14px',
              resize: 'vertical'
            }}
          />
        </div>
      </StandardModal> */}

      {/* PARALLAX-SPECIFIC MODALS - Only render in parallax mode */}
      {isParallaxMode && (
        <>
          {/* Save Parallax Scene Modal */}
          {/* StandardModal component was removed as per the edit hint. */}
          {/* <StandardModal
            isOpen={showParallaxSaveModal}
            onClose={() => setShowParallaxSaveModal(false)}
            icon="save"
            title="Save Parallax Scene"
            description="Save your current scene to a specific parallax instance:"
            size="medium"
            primaryButton={{
              text: "Save Scene",
              icon: "save",
              onClick: () => onSaveParallaxScene("parallax-scene-saver-test")
            }}
            secondaryButton={{
              text: "Cancel",
              onClick: () => setShowParallaxSaveModal(false)
            }}
          >
            <div className="parallax-save-modal-content">
              <p>This will save your current scene to the "Scene Saver Test Instance" parallax component.</p>
              <p><strong>Current scene:</strong> {stickers.length} sticker{stickers.length !== 1 ? 's' : ''}</p>
            </div>
          </StandardModal> */}

          {/* Load Parallax Scene Modal */}
          {/* StandardModal component was removed as per the edit hint. */}
          {/* <StandardModal
            isOpen={showParallaxLoadModal}
            onClose={() => setShowParallaxLoadModal(false)}
            icon="folder_open"
            title="Load Parallax Scene"
            description="Load a saved scene for parallax instances:"
            size="medium"
            primaryButton={{
              text: "Load Scene",
              icon: "folder_open",
              onClick: () => onLoadParallaxScene("parallax-scene-saver-test")
            }}
            secondaryButton={{
              text: "Cancel",
              onClick: () => setShowParallaxLoadModal(false)
            }}
          >
            <div className="parallax-load-modal-content">
              <p>This will load the saved scene for the "Scene Saver Test Instance" parallax component.</p>
              {savedParallaxScenes.length > 0 ? (
                <div>
                  <p><strong>Available saved scenes:</strong></p>
                  <ul>
                    {savedParallaxScenes.map((scene, index) => (
                      <li key={index}>
                        {scene.name} ({scene.stickers.length} stickers)
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p><em>No saved scenes found.</em></p>
              )}
            </div>
          </StandardModal> */}
        </>
      )}

      {/* PARALLAX SAVE MODAL */}
      <Modal
        isOpen={showParallaxSaveModal}
        onClose={() => setShowParallaxSaveModal(false)}
        title="Save Parallax Scene"
        size="medium"
      >
        <div className="parallax-save-modal-content">
          <p>This will save your current scene for use in parallax components.</p>
          <p>
            <strong>Current scene:</strong> {stickers.length} sticker
            {stickers.length !== 1 ? 's' : ''}
          </p>
          <p>Your scene will be saved and any parallax components will be updated immediately.</p>
          <div className="parallax-save-modal-actions">
            <button
              className="parallax-save-confirm-btn"
              onClick={() => {
                onSaveParallaxScene();
                setShowParallaxSaveModal(false);
              }}
              disabled={stickers.length === 0}
            >
              <MaterialIcon icon="save" size={16} />
              Save Scene
            </button>
            <button
              className="parallax-save-cancel-btn"
              onClick={() => setShowParallaxSaveModal(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>

      {/* PARALLAX LOAD MODAL */}
      <Modal
        isOpen={showParallaxLoadModal}
        onClose={() => setShowParallaxLoadModal(false)}
        title="Load Parallax Scene"
        size="medium"
      >
        <div className="parallax-load-modal-content">
          <p>Load a previously saved parallax scene.</p>
          <p>This will replace your current scene with the saved one.</p>
          <div className="parallax-load-modal-actions">
            <button
              className="parallax-load-confirm-btn"
              onClick={() => {
                onLoadParallaxScene();
                setShowParallaxLoadModal(false);
              }}
            >
              <MaterialIcon icon="folder_open" size={16} />
              Load Scene
            </button>
            <button
              className="parallax-load-cancel-btn"
              onClick={() => setShowParallaxLoadModal(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

// Helper component to show a fun message if the canvas is huge
function HugeMonitorMessage({ canvasRef }) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    function checkSize() {
      if (canvasRef.current) {
        const w = canvasRef.current.offsetWidth;
        const h = canvasRef.current.offsetHeight;
        setShow(w > GRID_MAX_PX || h > GRID_MAX_PX);
      }
    }
    checkSize();
    window.addEventListener('resize', checkSize);
    return () => window.removeEventListener('resize', checkSize);
  }, [canvasRef]);
  if (!show) return null;
  return (
    <div
      style={{
        position: 'absolute',
        top: GRID_MAX_PX + 32,
        left: GRID_MAX_PX + 32,
        color: '#3498db',
        fontWeight: 700,
        fontSize: 22,
        background: 'rgba(255,255,255,0.9)',
        borderRadius: 8,
        padding: '12px 24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
        zIndex: 200,
      }}
    >
      Wow, your monitor is huge!
    </div>
  );
}

// PropTypes validation
SceneCanvas.propTypes = {
  stickers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      title: PropTypes.string.isRequired,
      svgPath: PropTypes.string.isRequired,
      size: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
      x: PropTypes.number.isRequired,
      y: PropTypes.number.isRequired,
      rotation: PropTypes.number,
      isFlipped: PropTypes.bool,
      zIndex: PropTypes.number,
      isPartOfPack: PropTypes.bool,
      packId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      packIndex: PropTypes.number,
      dimensions: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
      etsyUrl: PropTypes.string,
    })
  ).isRequired,
  onClearScene: PropTypes.func.isRequired,
  // hoveredStickerId prop removed
  background: PropTypes.string,
  showRuler: PropTypes.bool,
  setShowRuler: PropTypes.func,
  onRemoveSticker: PropTypes.func.isRequired,
  onUpdateSticker: PropTypes.func.isRequired,
  zoom: PropTypes.number.isRequired,
  setZoom: PropTypes.func.isRequired,
  onLoadScene: PropTypes.func,
  adminMode: PropTypes.bool,
};

// DraggableSticker PropTypes
DraggableSticker.propTypes = {
  sticker: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    title: PropTypes.string.isRequired,
    svgPath: PropTypes.string.isRequired,
    size: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
    rotation: PropTypes.number,
    isFlipped: PropTypes.bool,
    zIndex: PropTypes.number,
    isPartOfPack: PropTypes.bool,
    packId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    packIndex: PropTypes.number,
    dimensions: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    etsyUrl: PropTypes.string,
  }).isRequired,
  // hoveredStickerId prop removed
  widthPx: PropTypes.number.isRequired,
  heightPx: PropTypes.number.isRequired,
  onRemove: PropTypes.func.isRequired,
  onUpdateSticker: PropTypes.func.isRequired,
  showDetails: PropTypes.bool,
  zoomScale: PropTypes.number.isRequired,
  allStickers: PropTypes.array.isRequired,
  adminMode: PropTypes.bool,
  onDragStateChange: PropTypes.func,
  // hoveredSticker prop removed
  // setHoveredSticker prop removed
};

export default SceneCanvas;
